import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getMyParticipations, getMyActivities } from '@/services/activityService';
import { getMyProfile } from '@/services/userService';
import { getMyCoinPosts } from '@/services/coinService';
import { apiActivityToFront, apiCoinPostToFront } from '@/hooks/useFeed';
import type { Activity, CoinPost } from '@/lib/types';
import MyActivityCard from '@/pages/my-activities/components/MyActivityCard';
import PostCard from '@/pages/le-coin/components/PostCard';
import ProfileHeader from './components/ProfileHeader';
import Footer from '@/components/feature/Footer';
import logoYonyonHeader from '@/assets/logo-yonyon-header-300x100.png';
import { authResendConfirmation } from '@/services/authService';
import EmailConfirmationNotice from './components/EmailConfirmationNotice';

type MainTab = 'activities' | 'publications';
type ActivitySubTab = 'joined' | 'created';

export default function Profile() {
  const { user, refreshUser } = useAuth();

  const [bio, setBio] = useState<string | null>(null);
  const [joined, setJoined] = useState<Activity[]>([]);
  const [created, setCreated] = useState<Activity[]>([]);
  const [publications, setPublications] = useState<CoinPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [tab, setTab] = useState<MainTab>('activities');
  const [subTab, setSubTab] = useState<ActivitySubTab>('joined');
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);
  const [resendingConfirmation, setResendingConfirmation] = useState(false);

  const showToast = (msg: string) => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = window.setTimeout(() => setToast(null), 3200);
  };

  const loadData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setLoadError(null);

    const [profileRes, joinedRes, createdRes, postsRes] = await Promise.allSettled([
      getMyProfile(),
      getMyParticipations(),
      getMyActivities(),
      getMyCoinPosts(),
    ]);

    if (profileRes.status === 'fulfilled') {
      setBio(profileRes.value.bio ?? null);
    } else {
      const msg =
        profileRes.reason instanceof Error
          ? profileRes.reason.message
          : 'Impossible de charger ton profil';
      setLoadError(msg);
    }

    setJoined(joinedRes.status === 'fulfilled' ? joinedRes.value.map(apiActivityToFront) : []);
    setCreated(createdRes.status === 'fulfilled' ? createdRes.value.map(apiActivityToFront) : []);
    setPublications(postsRes.status === 'fulfilled' ? postsRes.value.map(apiCoinPostToFront) : []);

    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUpdated = () => {
    refreshUser();
    loadData();
  };


  const handleResendConfirmation = async () => {
    if (!user || resendingConfirmation) return;

    setResendingConfirmation(true);

    try {
      const response = await authResendConfirmation(user.email);
      showToast(response.message);
    } catch {
      showToast("Impossible d'effectuer la demande. Réessaie dans quelques instants.");
    } finally {
      setResendingConfirmation(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <TopBar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-sm">
            <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
              <i className="ri-user-line text-2xl"></i>
            </div>
            <h1 className="mt-4 font-heading text-xl font-semibold text-foreground-950">
              Connecte-toi pour voir ton profil
            </h1>
            <p className="mt-1 text-sm text-foreground-500">
              Ton pseudo, ta bio et tes activités apparaîtront ici.
            </p>
            <Link
              to="/auth"
              className="mt-5 inline-block px-4 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Se connecter
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const activities = subTab === 'joined' ? joined : created;

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <TopBar />

      <main className="flex-1 w-full px-4 md:px-8 py-6 md:py-8">
        <div className="max-w-3xl mx-auto">
          <ProfileHeader
            user={user}
            bio={bio}
            onUpdated={handleUpdated}
            onNotify={showToast}
          />

          {!user.emailConfirmed && (
            <EmailConfirmationNotice
              onResend={handleResendConfirmation}
              resending={resendingConfirmation}
            />
          )}

          {/* Statistiques */}
          <div className="mt-4 grid grid-cols-3 gap-3">
            <StatCard
              icon="ri-calendar-check-line"
              value={joined.length + created.length}
              label="Activités"
            />
            <StatCard
              icon="ri-chat-smile-3-line"
              value={publications.length}
              label="Publications"
            />
            <StatCard
              icon="ri-heart-3-line"
              value={publications.reduce((sum, p) => sum + (p.likes || 0), 0)}
              label="Réactions reçues"
            />
          </div>

          {/* Main tabs */}
          <div className="mt-6 inline-flex rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setTab('activities')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${tab === 'activities'
                ? 'bg-white text-foreground-950'
                : 'text-foreground-500 hover:text-foreground-700'
                }`}
            >
              Mes activités
            </button>
            <button
              type="button"
              onClick={() => setTab('publications')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${tab === 'publications'
                ? 'bg-white text-foreground-950'
                : 'text-foreground-500 hover:text-foreground-700'
                }`}
            >
              Mes publications ({publications.length})
            </button>
          </div>

          {/* Activities sub-tabs */}
          {tab === 'activities' && (
            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSubTab('joined')}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${subTab === 'joined'
                  ? 'bg-foreground-900 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
              >
                Rejointes ({joined.length})
              </button>
              <button
                type="button"
                onClick={() => setSubTab('created')}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${subTab === 'created'
                  ? 'bg-foreground-900 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
              >
                Créées ({created.length})
              </button>
            </div>
          )}

          {/* Content */}
          <div className="mt-6 flex flex-col gap-4">
            {loading ? (
              [1, 2].map((i) => (
                <div key={i} className="bg-white border border-background-200 rounded-lg p-5 animate-pulse">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-lg bg-background-100 shrink-0"></div>
                    <div className="flex-1">
                      <div className="h-3 bg-background-100 rounded w-24 mb-2"></div>
                      <div className="h-5 bg-background-100 rounded w-3/4"></div>
                    </div>
                  </div>
                </div>
              ))
            ) : tab === 'activities' ? (
              activities.length === 0 ? (
                <EmptyState
                  icon={subTab === 'joined' ? 'ri-compass-3-line' : 'ri-add-circle-line'}
                  title={
                    subTab === 'joined'
                      ? 'Aucune activité rejointe'
                      : 'Aucune activité créée'
                  }
                  text={
                    subTab === 'joined'
                      ? 'Explore le fil et rejoins une sortie, un sport ou un coup de main.'
                      : 'Propose une activité et fais venir des gens près de chez toi.'
                  }
                  cta={{ to: '/', label: 'Explorer les activités' }}
                />
              ) : (
                activities.map((a) => (
                  <MyActivityCard
                    key={a.id}
                    activity={a}
                    created={subTab === 'created'}
                  />
                ))
              )
            ) : publications.length === 0 ? (
              <EmptyState
                icon="ri-chat-smile-3-line"
                title="Aucune publication"
                text="Tu n'as encore rien posté sur Le Coin."
                cta={{ to: '/le-coin', label: 'Aller sur Le Coin' }}
              />
            ) : (
              publications.map((post, index) => (
                <PostCard key={post.id} post={post} index={index} onNotify={showToast} />
              ))
            )}

            {loadError && (
              <div className="bg-white border border-background-200 rounded-lg p-5 text-center">
                <p className="text-sm text-foreground-500">{loadError}</p>
                <button
                  type="button"
                  onClick={loadData}
                  className="mt-3 px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Réessayer
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-scale-in">
          <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-foreground-900 text-white text-sm font-medium">
            <i className="ri-checkbox-circle-line text-accent-400 text-lg"></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, value, label }: { icon: string; value: number; label: string }) {
  return (
    <div className="bg-white border border-background-200 rounded-lg px-3 py-4 text-center">
      <div className="w-9 h-9 mx-auto rounded-full bg-accent-100 text-accent-700 flex items-center justify-center">
        <i className={`${icon} text-base`}></i>
      </div>
      <div className="mt-2 font-heading text-2xl font-bold text-foreground-950">{value}</div>
      <div className="mt-0.5 text-xs text-foreground-500 whitespace-nowrap">{label}</div>
    </div>
  );
}

function TopBar() {
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-background-200">
      <div className="px-4 md:px-8 h-[4.5rem] flex items-center justify-between gap-4">
        <div className="flex w-full items-center justify-between gap-3  h-[4.5rem]">
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-base"></i>
            Retour
          </Link>

          <Link
            to="/"
            className="
              block overflow-hidden
              lg:flex lg:overflow-visible
              items-center cursor-pointer bg-white rounded-lg px-2 py-1
            "
          >
            <img
              src={logoYonyonHeader}
              alt="Yonyon"
              className="h-16 w-auto object-contain translate-x-[2.5rem] lg:translate-x-0"
            />
          </Link>
        </div>
      </div>
    </header>
  );
}

function EmptyState({ icon, title, text, cta, }: { icon: string; title: string; text: string; cta: { to: string; label: string } }) {

  return (
    <div className="bg-white border border-background-200 rounded-lg p-10 text-center">
      <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
        <i className={`${icon} text-2xl`}></i>
      </div>
      <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">{title}</h2>
      <p className="mt-1 text-sm text-foreground-500 max-w-xs mx-auto">{text}</p>
      <Link
        to={cta.to}
        className="mt-4 inline-block px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
      >
        {cta.label}
      </Link>
    </div>
  );
}